import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Prereqs section — lists an entity's prerequisites and provides an "add"
 * form filtered to the entity's grade. The server's /admin/prereqs/candidates
 * endpoint returns lessons + blocking assessments valid for THIS target
 * (same grade for lessons; same-grade-or-unattached for blockings); this
 * component only renders what the server offers, so the cross-grade guard
 * is symmetric on both ends (UI can't propose what the server would reject).
 *
 * The lesson picker is a track→lesson Cascader — grade is implicit from the
 * page context, so the cascade skips it (spec).
 *
 * Extracted from the old cross-cutting /prereqs screen; delete + create
 * both toast, cycle/optional-required/cross-grade errors are humanized.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Cascader, Empty, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { createPrereq, deletePrereq, getPrereqCandidates, listPrereqs, } from '../../api';
import { notifySuccess } from '../errors';
export function PrereqsSection({ target, targetId, title }) {
    const [prereqs, setPrereqs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [candidates, setCandidates] = useState(null);
    const reloadPrereqs = useCallback(async () => {
        setLoading(true);
        try {
            setPrereqs(await listPrereqs(target, targetId));
        }
        catch { /* client auto-surfaces */ }
        finally {
            setLoading(false);
        }
    }, [target, targetId]);
    useEffect(() => { void reloadPrereqs(); }, [reloadPrereqs]);
    // Candidates are re-fetched whenever the prereq list changes: adding a
    // prereq can bind a previously-unattached blocking assessment to this
    // grade, which shifts the candidate pool. Cheaper to refetch than to
    // reproduce the server-side resolution client-side.
    const reloadCandidates = useCallback(async () => {
        try {
            setCandidates(await getPrereqCandidates(target, targetId));
        }
        catch { /* client auto-surfaces */ }
    }, [target, targetId]);
    useEffect(() => { void reloadCandidates(); }, [reloadCandidates, prereqs.length]);
    /**
     * Resolve a prereq row's "requires" side against the candidates pool.
     * Returns either a nice label OR a `broken: true` sentinel — the caller
     * renders broken refs with a red warning so dangling polymorphic rows
     * (the exact class that caused the math-DB contamination) look VISIBLY
     * broken instead of blending in as ordinary entries.
     *
     * The candidates endpoint already returns everything Sam is allowed to
     * add as a prereq for this scope. If a ref isn't in the pool, the row
     * either points at a deleted entity or lives outside the current grade —
     * both are actionable ("delete this row"), not "keep silently".
     */
    const resolveRequires = (p) => {
        if (p.requires === 'lesson') {
            const l = candidates?.lessons.find((x) => x.id === p.requires_id);
            if (l)
                return { label: `${l.track_name} · ${l.title}`, broken: false };
            return { label: `שיעור #${p.requires_id}`, broken: true };
        }
        const b = candidates?.blockings.find((x) => x.id === p.requires_id);
        if (b)
            return { label: b.name ?? b.topic_label ?? `בוחן חוסם #${b.id}`, broken: false };
        return { label: `בוחן חוסם #${p.requires_id}`, broken: true };
    };
    const cols = [
        { title: 'סוג', dataIndex: 'requires', width: 100, render: (v) => v === 'lesson' ? 'שיעור' : 'בוחן חוסם' },
        {
            title: 'דרוש',
            render: (_, p) => {
                const { label, broken } = resolveRequires(p);
                if (!broken)
                    return label;
                // Broken ref: red tag + warning icon + tooltip with the "why" +
                // hint that the row can be safely deleted. The whole cell is
                // dimmed to signal "not-a-real-thing-here" at a glance.
                return (_jsx(Tooltip, { title: "\u05D4\u05D4\u05E4\u05E0\u05D9\u05D4 \u05DE\u05E6\u05D1\u05D9\u05E2\u05D4 \u05E2\u05DC \u05D9\u05E9\u05D5\u05EA \u05E9\u05DC\u05D0 \u05E7\u05D9\u05D9\u05DE\u05EA (\u05D0\u05D5 \u05E0\u05DE\u05D7\u05E7\u05D4 \u05DE\u05D7\u05D5\u05E5 \u05DC\u05D4\u05E7\u05E9\u05E8 \u05D4\u05D6\u05D4). \u05DE\u05D5\u05DE\u05DC\u05E5 \u05DC\u05DE\u05D7\u05D5\u05E7 \u05D0\u05EA \u05D4\u05EA\u05E0\u05D0\u05D9.", children: _jsxs(Space, { size: 4, children: [_jsx(Tag, { icon: _jsx(WarningOutlined, {}), color: "error", children: "\u05D4\u05E4\u05E0\u05D9\u05D4 \u05E9\u05D1\u05D5\u05E8\u05D4" }), _jsx(Typography.Text, { type: "secondary", delete: true, children: label })] }) }));
            },
        },
        {
            title: 'פעולות', width: 90, fixed: 'right',
            render: (_, p) => (_jsx(Popconfirm, { title: "\u05DC\u05DE\u05D7\u05D5\u05E7 \u05EA\u05E0\u05D0\u05D9?", okText: "\u05DE\u05D7\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: async () => {
                    try {
                        await deletePrereq(p.id);
                        notifySuccess('תנאי נמחק');
                        reloadPrereqs();
                    }
                    catch { /* client auto-surfaces */ }
                }, children: _jsx(Button, { size: "small", danger: true, icon: _jsx(DeleteOutlined, {}) }) })),
        },
    ];
    return (_jsxs(Card, { title: title ?? 'תנאי-קדם', children: [prereqs.length === 0 && !loading
                ? _jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05EA\u05E0\u05D0\u05D9-\u05E7\u05D3\u05DD", image: Empty.PRESENTED_IMAGE_SIMPLE })
                : _jsx(Table, { size: "small", rowKey: "id", pagination: false, loading: loading, dataSource: prereqs, columns: cols }), candidates && (_jsx(AddPrereqForm, { target: target, targetId: targetId, lessons: candidates.lessons, blockings: candidates.blockings, onDone: () => { reloadPrereqs(); } }))] }));
}
function AddPrereqForm({ target, targetId, lessons, blockings, onDone }) {
    const [reqKind, setReqKind] = useState('lesson');
    const [reqId, setReqId] = useState(null);
    const [busy, setBusy] = useState(false);
    // Track → lesson cascade. Grade is implicit from page context (spec says
    // "cascade can skip the grade level entirely"), so the tree is just two
    // levels: pick a track, then a lesson inside it.
    const lessonCascadeOptions = useMemo(() => {
        const byTrack = new Map();
        for (const l of lessons) {
            if (target === 'lesson' && l.id === targetId)
                continue; // exclude self
            const g = byTrack.get(l.track_id) ?? { track_id: l.track_id, track_name: l.track_name, lessons: [] };
            g.lessons.push(l);
            byTrack.set(l.track_id, g);
        }
        return Array.from(byTrack.values()).map((g) => ({
            value: g.track_id,
            label: g.track_name,
            children: g.lessons.map((l) => ({
                value: l.id,
                label: l.title + (l.is_optional ? ' [רשות]' : ''),
            })),
        }));
    }, [lessons, target, targetId]);
    // For a fresh reqKind pick, reset the id — the previous value is meaningless
    // in the new pool.
    const changeKind = (v) => { setReqKind(v); setReqId(null); };
    const submit = async () => {
        if (!reqId)
            return;
        setBusy(true);
        try {
            await createPrereq({ target, target_id: targetId, requires: reqKind, requires_id: reqId });
            notifySuccess('תנאי-קדם נוסף');
            setReqId(null);
            onDone();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setBusy(false);
        }
    };
    return (_jsxs(Space, { wrap: true, style: { marginTop: 12 }, children: [_jsx("span", { children: "\u05D4\u05D5\u05E1\u05E3 \u05EA\u05E0\u05D0\u05D9:" }), _jsx(Select, { value: reqKind, onChange: changeKind, options: [{ value: 'lesson', label: 'שיעור' }, { value: 'assessment', label: 'בוחן חוסם' }], style: { width: 140 } }), reqKind === 'lesson' ? (_jsx(Cascader, { placeholder: "\u05D1\u05D7\u05E8 \u05E9\u05D9\u05E2\u05D5\u05E8 (\u05DE\u05E1\u05DC\u05D5\u05DC \u2192 \u05E9\u05D9\u05E2\u05D5\u05E8)", value: findLessonPath(lessonCascadeOptions, reqId), onChange: (vals) => setReqId(vals && vals.length === 2 ? Number(vals[1]) : null), options: lessonCascadeOptions, style: { minWidth: 340 }, showSearch: { filter: (input, path) => path.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }, displayRender: (labels) => labels.join(' · '), expandTrigger: "hover" })) : (_jsx(Select, { placeholder: "\u05D1\u05D7\u05E8 \u05D1\u05D5\u05D7\u05DF \u05D7\u05D5\u05E1\u05DD", value: reqId ?? undefined, onChange: (v) => setReqId(Number(v)), options: blockings.map((b) => ({ value: b.id, label: b.name ?? b.topic_label ?? `בוחן חוסם #${b.id}` })), style: { minWidth: 260 }, showSearch: true, optionFilterProp: "label" })), _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), loading: busy, disabled: !reqId, onClick: submit, children: "\u05D4\u05D5\u05E1\u05E3" })] }));
}
/**
 * Locate the [track_id, lesson_id] path for a given lesson id inside the
 * built cascade tree. Cascader is controlled — it needs the full path, not
 * just the leaf value.
 */
function findLessonPath(tree, leafId) {
    if (leafId == null)
        return undefined;
    for (const track of tree) {
        for (const lesson of track.children ?? []) {
            if (lesson.value === leafId)
                return [track.value, lesson.value];
        }
    }
    return undefined;
}
